import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import CualappLogo from './CualappLogo';

const AuthComponent = ({ onLogin, onRegister }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', fullName: '', phone: '', inviteCode: '', acceptTerms: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [codeStatus, setCodeStatus] = useState({ state: 'idle', info: null, message: '' });

  useEffect(() => {
    axios.get(`${API}/onboarding/policy`).then(r => setPolicy(r.data)).catch(() => {});
  }, []);

  // Validacion en tiempo real del codigo (debounce simple)
  useEffect(() => {
    if (isLogin) return;
    const code = formData.inviteCode.trim();
    const email = formData.email.trim();
    if (!code || !email) {
      setCodeStatus({ state: 'idle', info: null, message: '' });
      return;
    }
    setCodeStatus({ state: 'checking', info: null, message: 'Verificando...' });
    const handler = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/onboarding/validate-code`, { code, email });
        setCodeStatus({
          state: 'valid',
          info: r.data,
          message: `Codigo valido${r.data.issued_by ? ` - Invitado por ${r.data.issued_by}` : ''}`,
        });
      } catch (err) {
        setCodeStatus({
          state: 'invalid',
          info: null,
          message: err.response?.data?.detail || 'Codigo invalido',
        });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [formData.inviteCode, formData.email, isLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      let result;
      if (isLogin) {
        result = await onLogin(formData.email, formData.password);
      } else {
        if (!formData.acceptTerms) {
          setError('Debes aceptar los Terminos y la Politica de Privacidad.');
          setLoading(false);
          return;
        }
        result = await onRegister(
          formData.email,
          formData.password,
          formData.fullName,
          formData.phone,
          formData.inviteCode.trim().toUpperCase(),
          formData.acceptTerms
        );
      }
      if (!result.success) { setError(result.error); }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };
  const requiresCode = policy?.requires_invite_code !== false; // default true

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mb-4 flex flex-col items-center">
            <CualappLogo size={88} animated={true} />
            <h1 className="mt-4 text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">CUALAPP</h1>
            <p className="text-sm text-gray-500 mt-1">Ecosistema Financiero Integral</p>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{isLogin ? 'Iniciar Sesion' : 'Crear Cuenta'}</h2>
          <p className="mt-2 text-sm text-gray-600">{isLogin ? 'Accede a tu wallet y transacciones' : 'Unete al ecosistema CUALAPP'}</p>
        </div>
        <form className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-md" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} data-testid="auth-email-input" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">Contrasena</label>
              <div className="mt-1 relative">
                <input id="password" name="password" type={showPassword ? "text" : "password"} required value={formData.password} onChange={handleChange} data-testid="auth-password-input" className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500" />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                  <span className="text-gray-400 hover:text-gray-600 cursor-pointer select-none">
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L8.75 8.75m1.128 1.128l4.242 4.242M12.093 5.25c.775 0 1.544.068 2.307.204a9.97 9.97 0 014.208 2.546" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" /></svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </span>
                </button>
              </div>
              {!isLogin && <p className="mt-1 text-xs text-gray-500">La contrasena debe tener al menos 8 caracteres</p>}
            </div>
            {!isLogin && (
              <>
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Nombre Completo</label>
                  <input id="fullName" name="fullName" type="text" required value={formData.fullName} onChange={handleChange} data-testid="auth-fullname-input" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Telefono (Opcional)</label>
                  <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} data-testid="auth-phone-input" className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500" />
                </div>
                {requiresCode && (
                  <div>
                    <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700">
                      Codigo de invitacion <span className="text-red-500">*</span>
                    </label>
                    <input id="inviteCode" name="inviteCode" type="text" required value={formData.inviteCode} onChange={handleChange}
                      data-testid="auth-invite-code-input"
                      placeholder="CUAL-XXXXXXXXXX"
                      style={{ textTransform: 'uppercase' }}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500" />
                    {codeStatus.state === 'checking' && (
                      <p className="mt-1 text-xs text-gray-500" data-testid="auth-code-checking">{codeStatus.message}</p>
                    )}
                    {codeStatus.state === 'valid' && (
                      <p className="mt-1 text-xs text-green-600" data-testid="auth-code-valid">{codeStatus.message}</p>
                    )}
                    {codeStatus.state === 'invalid' && (
                      <p className="mt-1 text-xs text-red-600" data-testid="auth-code-invalid">{codeStatus.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      ¿No tienes codigo? <a href="/onboarding" data-testid="auth-request-invite-link" className="text-purple-600 hover:underline">Solicita acceso al Soberano</a>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          {!isLogin && (
            <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="auth-terms-label">
              <input type="checkbox" name="acceptTerms" checked={formData.acceptTerms} onChange={handleChange}
                data-testid="auth-accept-terms-checkbox"
                className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" />
              <span className="text-xs text-gray-600 leading-relaxed">
                He leido y acepto los <a href="/terms-beta" target="_blank" rel="noopener noreferrer" data-testid="auth-terms-link" className="text-purple-600 hover:underline">Terminos de la Beta</a> y la <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="auth-privacy-link" className="text-purple-600 hover:underline">Politica de Privacidad</a>.
              </span>
            </label>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded" data-testid="auth-error-message">{error}</div>}
          <div>
            <button type="submit" disabled={loading || (!isLogin && requiresCode && codeStatus.state === 'invalid') || (!isLogin && !formData.acceptTerms)} data-testid="auth-submit-button" className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 transition-all duration-200">
              {loading ? 'Procesando...' : (isLogin ? 'Iniciar Sesion' : 'Crear Cuenta')}
            </button>
          </div>
          <div className="text-center">
            <button type="button" onClick={() => setIsLogin(!isLogin)} data-testid="auth-toggle-mode" className="text-sm text-purple-600 hover:text-purple-500">
              {isLogin ? 'No tienes cuenta? Registrate' : 'Ya tienes cuenta? Inicia sesion'}
            </button>
          </div>
          {isLogin && (
            <>
              <div className="text-center">
                <a href="/forgot-password" data-testid="auth-forgot-link" className="text-xs text-gray-500 hover:text-purple-600">
                  ¿Olvidaste tu contrasena?
                </a>
              </div>
              {requiresCode && (
                <div className="text-center">
                  <a href="/onboarding" data-testid="auth-onboarding-link" className="text-xs text-gray-500 hover:text-purple-600">
                    Solicitar acceso a la Beta Cerrada
                  </a>
                </div>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default AuthComponent;
